import React, { useState, useEffect, useCallback } from 'react';
import { api, useLanguage } from '../context/AppContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Trophy, Dumbbell, Calendar, ChevronRight, ChevronLeft, Loader2,
  TrendingUp, Filter, ArrowUp
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';
import GlowChartDefs, { GLOW_FILTER, glowTooltipStyle, glowTooltipItemStyle, glowTooltipLabelStyle, themeRgb } from '../components/charts/GlowChartDefs';

const MUSCLE_GROUPS = [
  { key: 'all', label_fr: 'Tous', label_en: 'All' },
  { key: 'push', label_fr: 'Push', label_en: 'Push' },
  { key: 'pull', label_fr: 'Pull', label_en: 'Pull' },
  { key: 'legs', label_fr: 'Legs', label_en: 'Legs' },
  { key: 'core', label_fr: 'Core', label_en: 'Core' },
  { key: 'cardio', label_fr: 'Cardio', label_en: 'Cardio' },
];

const PUSH_KEYWORDS = ['bench', 'press', 'push', 'shoulder', 'dip', 'fly', 'chest', 'tricep', 'overhead', 'developpe', 'presse'];
const PULL_KEYWORDS = ['row', 'pull', 'curl', 'deadlift', 'lat', 'back', 'bicep', 'chin', 'tirage', 'traction'];
const LEG_KEYWORDS = ['squat', 'leg', 'lunge', 'calf', 'hamstring', 'quad', 'hip', 'glute', 'mollet', 'fente', 'jambe'];
const CORE_KEYWORDS = ['plank', 'crunch', 'ab', 'core', 'twist', 'gainage', 'oblique'];
const CARDIO_KEYWORDS = ['run', 'bike', 'row', 'jump', 'burpee', 'cardio', 'sprint', 'course', 'velo'];

function classifyExercise(name) {
  const n = String(name ?? '').toLowerCase();
  if (PUSH_KEYWORDS.some(k => n.includes(k))) return 'push';
  if (PULL_KEYWORDS.some(k => n.includes(k))) return 'pull';
  if (LEG_KEYWORDS.some(k => n.includes(k))) return 'legs';
  if (CORE_KEYWORDS.some(k => n.includes(k))) return 'core';
  if (CARDIO_KEYWORDS.some(k => n.includes(k))) return 'cardio';
  return 'other';
}

function formatDate(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

function formatShortDate(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' });
  } catch { return ''; }
}

export default function PowerRegister() {
  const { language } = useLanguage();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedExercise, setSelectedExercise] = useState(null);

  const t = useCallback((fr, en) => (language === 'fr' ? fr : en), [language]);

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const res = await api.get('/workouts/power-register');
        setRecords(res.data.records || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    fetchRecords();
  }, []);

  const filtered = filter === 'all'
    ? records
    : records.filter(r => classifyExercise(r.display_name) === filter);

  const totalPRs = records.length;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto pb-24 px-4" data-testid="power-register-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <Trophy className="w-7 h-7 text-amber-400" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-black uppercase tracking-tight text-foreground" data-testid="power-register-title">
                {t('Registre de Puissance', 'Power Register')}
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {t('Tes records personnels. Ta preuve de progression.', 'Your personal records. Your proof of progress.')}
              </p>
            </div>
          </div>

          {/* Stats summary — GLOW aesthetic (Iter. 411.bb) */}
          {!loading && (
            <div className="flex gap-3 mt-4">
              <div
                className="flex-1 p-3 rounded-xl text-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(245,158,11,0.22) 100%)',
                  border: '1px solid rgba(245,158,11,0.5)',
                  boxShadow: '0 0 14px rgba(245,158,11,0.4), inset 0 1px 0 rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <p
                  className="text-2xl font-black"
                  data-testid="total-prs"
                  style={{ color: '#fbbf24', textShadow: '0 0 8px rgba(245,158,11,0.65)' }}
                >
                  {totalPRs}
                </p>
                <p
                  className="text-[10px] uppercase tracking-widest font-black"
                  style={{ color: '#fef3c7', textShadow: '0 0 4px rgba(245,158,11,0.55)' }}
                >
                  {t('Records', 'Records')}
                </p>
              </div>
              <div
                className="flex-1 p-3 rounded-xl text-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(251,146,60,0.22) 100%)',
                  border: '1px solid rgba(251,146,60,0.5)',
                  boxShadow: '0 0 14px rgba(251,146,60,0.4), inset 0 1px 0 rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <p
                  className="text-2xl font-black"
                  data-testid="total-logs"
                  style={{ color: '#fb923c', textShadow: '0 0 8px rgba(251,146,60,0.65)' }}
                >
                  {records.reduce((sum, r) => sum + (r.total_logs || 0), 0)}
                </p>
                <p
                  className="text-[10px] uppercase tracking-widest font-black"
                  style={{ color: '#fed7aa', textShadow: '0 0 4px rgba(251,146,60,0.55)' }}
                >
                  {t('Logs Totaux', 'Total Logs')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Muscle Group Filter — GLOW aesthetic (Iter. 411.bb) */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1" data-testid="muscle-filter">
          {MUSCLE_GROUPS.map(g => {
            const active = filter === g.key;
            return (
              <button
                key={g.key}
                onClick={() => { setFilter(g.key); setSelectedExercise(null); }}
                data-testid={`filter-${g.key}`}
                className="px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest whitespace-nowrap transition-all active:scale-95 inline-flex items-center gap-1.5"
                style={{
                  background: active
                    ? 'linear-gradient(140deg, #fb923c 0%, #f97316 55%, #c2410c 100%)'
                    : 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(251,146,60,0.14) 100%)',
                  border: active
                    ? '1px solid rgba(251,146,60,0.8)'
                    : '1px solid rgba(251,146,60,0.35)',
                  boxShadow: active
                    ? '0 0 14px rgba(251,146,60,0.7), inset 0 1px 0 rgba(255,255,255,0.2)'
                    : '0 0 10px rgba(251,146,60,0.25), inset 0 1px 0 rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(6px)',
                  color: active ? '#fff' : '#fed7aa99',
                  textShadow: active
                    ? '0 0 6px rgba(255,255,255,0.55)'
                    : '0 0 4px rgba(251,146,60,0.4)',
                }}
              >
                <Filter
                  className="w-3 h-3"
                  style={{
                    filter: active
                      ? 'drop-shadow(0 0 5px rgba(255,255,255,0.75))'
                      : 'drop-shadow(0 0 4px rgba(251,146,60,0.65))',
                  }}
                />
                {language === 'fr' ? g.label_fr : g.label_en}
              </button>
            );
          })}
        </div>

        {/* Selected Exercise Chart */}
        {selectedExercise && (
          <Card className="mb-4 bg-card border-amber-500/30" data-testid="pr-chart">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-heading uppercase tracking-wider flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-amber-400" />
                  {t('Courbe Balistique', 'Ballistic Curve')} — {selectedExercise.display_name}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedExercise(null)}
                  data-testid="close-chart"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> {t('Retour', 'Back')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {selectedExercise.timeline?.length > 1 ? (
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={selectedExercise.timeline.map(t => ({
                      ...t,
                      date: formatShortDate(t.created_at),
                      label: `${t.weight} ${t.weight_unit || 'lbs'}`,
                    }))}>
                      <defs>
                        <GlowChartDefs id="powerLine" theme="amber" vertical />
                      </defs>
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} width={40} />
                      <Tooltip
                        cursor={{ stroke: `rgba(${themeRgb('amber')},0.35)`, strokeWidth: 1 }}
                        contentStyle={glowTooltipStyle(themeRgb('amber'))}
                        itemStyle={glowTooltipItemStyle()}
                        labelStyle={glowTooltipLabelStyle(themeRgb('amber'))}
                        formatter={(val, name) => [
                          `${val} ${selectedExercise.pr_unit || 'lbs'}`,
                          name === 'weight' ? t('Poids', 'Weight') : name
                        ]}
                      />
                      <Line
                        type="monotone"
                        dataKey="weight"
                        stroke="url(#powerLine-gradient)"
                        strokeWidth={3}
                        style={{ filter: GLOW_FILTER(themeRgb('amber')) }}
                        dot={(props) => {
                          const { cx, cy, payload } = props;
                          if (payload.is_pr) {
                            return <circle key={`pr-${cx}`} cx={cx} cy={cy} r={6} fill="#fcd34d" stroke="#f59e0b" strokeWidth={2} style={{ filter: GLOW_FILTER(themeRgb('amber')) }} />;
                          }
                          return <circle key={`dot-${cx}`} cx={cx} cy={cy} r={3} fill="#f59e0b" fillOpacity={0.7} />;
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  {t('Minimum 2 logs pour afficher la courbe.', 'Minimum 2 logs to display the curve.')}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Records List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <Dumbbell className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {filter === 'all'
                ? t('Aucun record. Logue tes seances pour commencer.', 'No records yet. Log your sessions to start.')
                : t('Aucun record dans ce groupe.', 'No records in this group.')}
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="records-list">
            {filtered.map((record, i) => {
              const group = classifyExercise(record.display_name);
              const groupColors = {
                push: 'text-red-400 bg-red-500/10 border-red-500/20',
                pull: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
                legs: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
                core: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
                cardio: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
                other: 'text-muted-foreground bg-secondary/50 border-border',
              };

              return (
                <button
                  key={record.exercise_name}
                  onClick={() => setSelectedExercise(record)}
                  className={`w-full p-3 rounded-xl border text-left transition-all hover:scale-[1.01] ${
                    selectedExercise?.exercise_name === record.exercise_name
                      ? 'border-amber-500/50 bg-amber-500/5 shadow-lg shadow-amber-500/10'
                      : 'border-border/50 bg-card hover:border-border'
                  }`}
                  data-testid={`record-${i}`}
                >
                  <div className="flex items-center gap-3">
                    {/* Rank */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black shrink-0 ${
                      i === 0 ? 'bg-amber-500/20 text-amber-400' :
                      i === 1 ? 'bg-zinc-400/20 text-zinc-300' :
                      i === 2 ? 'bg-orange-700/20 text-orange-400' :
                      'bg-secondary text-muted-foreground'
                    }`}>
                      {i < 3 ? <Trophy className="w-3.5 h-3.5" /> : i + 1}
                    </div>

                    {/* Exercise info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-foreground truncate">
                        {record.display_name || record.exercise_name}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase ${groupColors[group] || groupColors.other}`}>
                          {group}
                        </span>
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {formatDate(record.pr_date)}
                        </span>
                        {typeof record.rir_avg === 'number' && record.rir_samples > 0 && (
                          <span
                            className="text-[10px] text-primary/80 font-semibold"
                            data-testid={`pr-rir-avg-${i}`}
                            title={`RIR moyen sur ${record.rir_samples} derniere(s) serie(s)`}
                          >
                            RIR moy. {record.rir_avg}
                            {record.intensity_label ? <span className="text-muted-foreground/70 font-normal"> → {record.intensity_label}</span> : null}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* PR value */}
                    <div className="text-right shrink-0">
                      <p className="text-lg font-black text-amber-400" data-testid={`pr-weight-${i}`}>
                        {record.pr_weight} <span className="text-xs text-muted-foreground">{record.pr_unit}</span>
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {record.pr_sets}x{record.pr_reps} <ArrowUp className="w-3 h-3 inline text-emerald-400" /> {record.total_logs} logs
                      </p>
                    </div>

                    <ChevronRight className="w-4 h-4 text-muted-foreground/50 shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
